import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { useAddContractSearchParams } from '@/hooks/use-add-contract-search-params';

export type AddContractLock = 'all' | 'boards';

type AddContractContextValue = {
  lock: AddContractLock | undefined;
  isSheetOpen: boolean;
  channelId: number | undefined;
  channelName: string | undefined;
  setSheetOpen: (open: boolean) => void;
  setSelectedChannel: (id: number | undefined, name?: string) => void;
  openWithChannel: (id: number | undefined, name: string) => void;
};

type AddContractProviderProps = PropsWithChildren<{
  /**
   * The sheet's portal is outside the page overlay. Lock all actions or only
   * board selection here so URL parameters cannot bypass the page's lock.
   */
  lock?: AddContractLock;
}>;

const noop = () => {};

const AddContractContext = createContext<AddContractContextValue | undefined>(
  undefined,
);

export function AddContractProvider({
  lock,
  children,
}: AddContractProviderProps) {
  const {
    isSheetOpen,
    channelId,
    channelName,
    setSheetOpen,
    setSelectedChannel,
    openWithChannel,
  } = useAddContractSearchParams();

  useEffect(() => {
    if (lock === 'all' && isSheetOpen) {
      setSheetOpen(false);
    }
  }, [lock, isSheetOpen, setSheetOpen]);

  const value = useMemo(
    () => ({
      lock,
      isSheetOpen: isSheetOpen && lock !== 'all',
      channelId: lock ? undefined : channelId,
      channelName: lock ? undefined : channelName,
      setSheetOpen: lock === 'all' ? noop : setSheetOpen,
      setSelectedChannel: lock ? noop : setSelectedChannel,
      openWithChannel: lock ? noop : openWithChannel,
    }),
    [
      lock,
      isSheetOpen,
      channelId,
      channelName,
      setSheetOpen,
      setSelectedChannel,
      openWithChannel,
    ],
  );

  return (
    <AddContractContext.Provider value={value}>
      {children}
    </AddContractContext.Provider>
  );
}

export function useAddContractContext(): AddContractContextValue {
  const context = useContext(AddContractContext);
  if (context == null) {
    throw new Error(
      'useAddContractContext must be used within an AddContractProvider',
    );
  }
  return context;
}
